/**
 * 저장된 카드(결제 수단) API 서비스
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 *
 * PCI DSS 준수:
 * - 실제 카드 번호는 저장하지 않음
 * - Bloomin은 hosted widget 미제공으로 HTTPS 백엔드 토큰화 엔드포인트에 1회 전송
 * - 토큰만 서버에 저장
 *
 * @see olulo-mx-admin/app/Http/Controllers/Api/V1/PaymentMethodController.php
 */
import { apiClient } from './client';
import type {
  SavedCardListResponse,
  SavedCardResponse,
  CardCheckoutResponse,
  ConfirmCardRegistrationRequest,
  UpdateCardNicknameRequest,
  DeleteCardResponse,
  SetDefaultCardResponse,
} from './types';
import type { SavedCardsTranslation } from '@/i18n/savedCards';

/**
 * API 에러 코드를 번역 키로 매핑
 * 백엔드에서 반환하는 에러 코드를 프론트엔드 번역 키로 변환합니다.
 */
export const API_ERROR_CODE_MAP: Record<string, keyof SavedCardsTranslation> = {
  CARD_LIMIT_EXCEEDED: 'errorCardLimitExceeded',
  DUPLICATE_CARD: 'errorDuplicateCard',
  CARD_INACTIVE: 'errorCardInactive',
  CARD_EXPIRED: 'errorCardExpired',
  CARD_NOT_FOUND: 'errorCardNotFound',
  REGISTRATION_FAILED: 'errorRegistrationFailed',
};

/**
 * API 에러 코드를 번역 키로 변환
 *
 * @param errorCode - API에서 반환된 에러 코드
 * @returns 해당하는 번역 키 또는 undefined (알 수 없는 코드)
 */
export function getErrorTranslationKey(
  errorCode: string | undefined
): keyof SavedCardsTranslation | undefined {
  if (!errorCode) return undefined;
  return API_ERROR_CODE_MAP[errorCode];
}

export const savedCardsApi = {
  /**
   * 저장된 카드 목록 조회
   * GET /api/v1/payment-methods
   *
   * 활성화된 카드만 반환 (is_active=true)
   * 인증 필수 (auth:customer)
   */
  getCards: (signal?: AbortSignal) =>
    apiClient.get<SavedCardListResponse>('/api/v1/payment-methods', { signal }),

  /**
   * 카드 등록 체크아웃 생성
   * POST /api/v1/payment-methods
   *
   * Bloomin 카드 입력 세션을 생성합니다.
   * 고객당 최대 5개 카드까지 등록 가능합니다.
   *
   * @returns Bloomin 카드 입력 세션
   */
  createCheckout: () =>
    apiClient.post<CardCheckoutResponse>(
      '/api/v1/payment-methods'
    ),

  /**
   * 카드 등록 확인
   * POST /api/v1/payment-methods/confirm
   *
   * Bloomin 토큰화 API로 카드를 저장합니다.
   *
   * @param data - 카드 입력 payload와 선택적 별명
   */
  confirmRegistration: (data: ConfirmCardRegistrationRequest) =>
    apiClient.post<SavedCardResponse>(
      '/api/v1/payment-methods/confirm',
      data
    ),

  /**
   * 카드 별명 업데이트
   * PATCH /api/v1/payment-methods/{cardId}
   *
   * @param cardId - 카드 ID
   * @param data - 새 별명 (null로 설정하면 별명 삭제)
   */
  updateNickname: (cardId: number, data: UpdateCardNicknameRequest) =>
    apiClient.patch<SavedCardResponse>(
      `/api/v1/payment-methods/${cardId}`,
      data
    ),

  /**
   * 기본 카드 설정
   * POST /api/v1/payment-methods/{cardId}/default
   *
   * 해당 카드를 기본 결제 수단으로 설정합니다.
   * 만료되거나 비활성화된 카드는 기본 카드로 설정할 수 없습니다.
   *
   * @param cardId - 카드 ID
   */
  setDefault: (cardId: number) =>
    apiClient.post<SetDefaultCardResponse>(
      `/api/v1/payment-methods/${cardId}/default`
    ),

  /**
   * 카드 삭제
   * DELETE /api/v1/payment-methods/{cardId}
   *
   * Bloomin 저장 토큰을 삭제하고 DB에서도 삭제합니다.
   * 기본 카드 삭제 시 다른 카드가 자동으로 기본 카드가 됩니다.
   *
   * @param cardId - 카드 ID
   */
  deleteCard: (cardId: number) =>
    apiClient.delete<DeleteCardResponse>(
      `/api/v1/payment-methods/${cardId}`
    ),
};
